import {
    PriceQuantitiesChangePayload,
    PriceQuantity,
    SelectedArticle,
    SelectedArticlesChangePayload,
} from '@/containers/widget/useWidgetPreview';

export const PREVIEW_LOG_LIMIT = 50;

export type LogDirection = 'in' | 'out';

export interface PreviewLogEntry {
    at: Date;
    direction: LogDirection;
    text: string;
}

export const appendLogEntry = (
    entries: PreviewLogEntry[],
    entry: PreviewLogEntry
): PreviewLogEntry[] => [...entries, entry].slice(-PREVIEW_LOG_LIMIT);

export const describeSelectedArticles = (payload: SelectedArticlesChangePayload): string =>
    [
        'selectedArticlesChange',
        formatArticleCount(payload.articles),
        formatArticles(payload.articles),
    ]
        .filter((part): part is string => part !== null)
        .join(' · ');

export const describePriceQuantities = (payload: PriceQuantitiesChangePayload): string =>
    [
        'priceQuantitiesChange',
        formatPriceCount(payload.quantities),
        formatPriceQuantities(payload.quantities),
    ]
        .filter((part): part is string => part !== null)
        .join(' · ');

export const describeCommand = (verb: string, payload: unknown): string =>
    `${verb} ${JSON.stringify(payload)}`;

export const formatLogEntry = (entry: PreviewLogEntry): string =>
    `${formatTime(entry.at)} ${entry.direction === 'in' ? '←' : '→'} ${entry.text}`;

const formatArticleCount = (articles: SelectedArticle[]): string =>
    `${articles.length} ${articles.length === 1 ? 'article' : 'articles'}`;

const formatArticles = (articles: SelectedArticle[]): string | null =>
    articles.length > 0 ? articles.map(formatSelectedArticle).join(', ') : null;

export const formatSelectedArticle = (article: SelectedArticle): string =>
    `${article.ArticleId}×${article.Quantity} (${article.Duration === 0 ? 'add-on' : 'duration'})`;

const formatPriceCount = (quantities: PriceQuantity[]): string =>
    `${quantities.length} ${quantities.length === 1 ? 'price' : 'prices'}`;

const formatPriceQuantities = (quantities: PriceQuantity[]): string | null =>
    quantities.length > 0 ? quantities.map(formatPriceQuantity).join(', ') : null;

export const formatPriceQuantity = (quantity: PriceQuantity): string =>
    `#${quantity.PriceId}×${quantity.Quantity} (${formatPriceLabel(quantity)})`;

const formatPriceLabel = (quantity: PriceQuantity): string => {
    if (quantity.Category !== null) return quantity.Category;
    return quantity.PriceId === 0 ? 'unpriced' : 'price';
};

const formatTime = (date: Date): string =>
    [date.getHours(), date.getMinutes(), date.getSeconds()]
        .map((part) => String(part).padStart(2, '0'))
        .join(':');
